import axiosInstance from "../utils/axiosConfig";
import { useEffect, useState, useMemo, useCallback } from "react";
import BrAvatar from "./Avatar";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import Chip from "@material-ui/core/Chip";
import { Link } from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { usePusherConnection } from '../hooks/usePusherConnection';


export default function XCPals(props) {
    const { user, isParent, theme, showNotification } = props;
    
    const { isConnected, subscribeToPalNotifications } = usePusherConnection(user);
    
    const useStyles = makeStyles({
        button: {
            width: "80px",
            height: "25px",
            borderRadius: "12px",
            fontSize: "10px",
            margin: "5px"
        },
        root: {
            minHeight: 500,
            maxHeight: 750,
            width: "100%",
            maxWidth: 650,
            margin: "0 auto"
        },
        bullet: {
            minHeight: "100px",
            margin: "0 2px",
            transform: "scale(0.8)"
        },
        title: {
            fontSize: 32,
            fontFamily: "GoodUnicorn",
            color: theme.primaryColor,
            textAlign: "center",
            marginBottom: "5px",
            marginTop: "25px"
        },
        avatar: {
            marginLeft: "35%",
            marginTop: "10%",
            width: "20%",
            height: "20%"
        },
        palGrid: {
            minWidth: 300,
            display: "flex",
            justifyContent: "center"
        },
        palrow: {
            margin: 10,
            cursor: "pointer",
            "&:hover": {
                backgroundColor: "#f5f5f5"
            }
        },
        pos: {
            marginBottom: 12
        },
        chip1: {
            width: 110,
            fontSize: 12
        },
        chip2: {
            width: 120,
            fontSize: 12
        },
        toggle: {
            margin: "10px auto",
            padding: "5px",
            borderRadius: "15px",
            backgroundColor: "#fffefe",
            textAlign: "center",
            whiteSpace: "nowrap",
            display: "inline-block",
            width: "fit-content"
        },
        toggleContainer: {
            display: "flex",
            justifyContent: "center",
            marginBottom: "5px"
        },
        xcIcon: {
            margin: 10
        },
        xcName: {
            fontSize: 14,
            margin: 5,
            fontWeight: 500
        },
        homeGrid: {
            "&::before": {
                content: "' '",
                background: `url(${theme.backgroundLogo}) no-repeat center center fixed`,
                opacity: ".6",
                backgroundSize: "cover",
                position: "absolute",
                top: "160px",
                right: "0px",
                bottom: "0px",
                left: "0px",
                zIndex: -1,
            }
        },
        searchField: {
            width: "95%",
            margin: "10px auto",
            display: "block"
        },
        friendsList: {
            maxHeight: "500px",
            overflow: "auto",
            padding: "10px"
        }
    });
    
    const classes = useStyles(theme);
    const [toggleFriends, setToggleFriends] = useState(0); // Start with requests tab
    const [friends, setFriends] = useState([]);
    const [pendingFriends, setPendingFriends] = useState([]);
    const [searchString, setSearchString] = useState("");
    // Initialize approval state from localStorage and maintain persistence
    const [approvedByCurrentParent, setApprovedByCurrentParent] = useState(() => {
        const targetId = isParent && user.children?.length > 0 
            ? window.localStorage.getItem('SELECTED_CHILD_ID') 
            : user.id;
        const storageKey = `approved_friends_${user.id}_${targetId}`;
        const saved = localStorage.getItem(storageKey);
        return saved ? new Set(JSON.parse(saved)) : new Set();
         }); // Track which requests this parent has approved
    
    // Helper function to update approval state and persist to localStorage
    const updateApprovalState = useCallback((updateFunction) => {
        const targetId = isParent && user.children?.length > 0 
            ? window.localStorage.getItem('SELECTED_CHILD_ID') 
            : user.id;
        const storageKey = `approved_friends_${user.id}_${targetId}`;
        
        setApprovedByCurrentParent(prev => {
            const newSet = updateFunction(prev);
            // Persist to localStorage
            localStorage.setItem(storageKey, JSON.stringify([...newSet]));
            console.log('🚀 ~ updateApprovalState ~ Updated approval state:', {
                storageKey,
                newApprovals: [...newSet],
                userId: user.id,
                targetId: isParent && user.children?.length > 0 
                    ? window.localStorage.getItem('SELECTED_CHILD_ID') 
                    : user.id
            });
            return newSet;
        });
    }, [user.id, isParent]);
    
    // Modify refreshFriendData to handle parent-specific friend requests
    const refreshFriendData = useCallback(async () => {
        try {
            const targetId = isParent && user.children?.length > 0 
                ? window.localStorage.getItem('SELECTED_CHILD_ID') 
                : user.id;
            
            console.log('Refreshing friend data for:', {
                userId: targetId,
                isParent: isParent,
                toggleFriendsState: toggleFriends
            });
            
            if (toggleFriends === 0) {
                // Use parent-aware endpoint when parent is viewing
                // Use parentId (original parent ID) if available, otherwise fall back to user.id
                const parentId = user.parentId || user.id;
                const url = isParent ? 
                    `/pending_friends/${targetId}?parent_id=${parentId}` : 
                    `/pending_friends/${targetId}`;
                console.log('XCPals: Fetching pending friends with URL:', url);
                
                const result = await axiosInstance.get(url);
                console.log('Pending friends result:', result.data);
                setPendingFriends(result.data);
            } else {
                const result = await axiosInstance.get(`/friends/${targetId}`);
                console.log('Friends result:', result.data);
                setFriends(result.data);
            }
        } catch (error) {
            console.error('Error refreshing friend data:', error);
            showNotification('Failed to fetch friend requests', 'error');
        }
    }, [toggleFriends, user, showNotification, isParent]);
    
    // Subscribe to friend request notifications
    useEffect(() => {
        if (isConnected && user?.id) {
            const cleanup = subscribeToPalNotifications((data, eventType) => {
                console.log(`${eventType} Notification received for user:`, {
                    eventName: eventType,
                    data,
                    userContext: {
                        id: user?.id,
                        isParent: isParent,
                        selectedChildId: window.localStorage.getItem('SELECTED_CHILD_ID')
                    }
                });
                
                if (isParent) {
                    // Show notification for parents
                    showNotification(
                        `New friend request for your child from ${data.friend_name || 'someone'}`,
                        'info'
                    );
                }
                
                // Don't clear approval state on notifications - let it persist.
                
                // Force refresh of data
                setPendingFriends([]); // Clear first to force re-render
                refreshFriendData();
            });
            
            // Initial data fetch
            refreshFriendData();
            
            return () => {
                if (cleanup) cleanup();
            };
        }
    }, [isConnected, user, refreshFriendData, showNotification]);
    
    useEffect(() => {
        // Don't clear approval state when switching tabs - let it persist
        refreshFriendData();
    }, [toggleFriends, refreshFriendData]);
    
    useEffect(() => {
        setToggleFriends(0);
    }, []);
    
    // Re-initialize approval state when user or selected child changes
    useEffect(() => {
        const targetId = isParent && user.children?.length > 0 
            ? window.localStorage.getItem('SELECTED_CHILD_ID') 
            : user.id;
        const storageKey = `approved_friends_${user.id}_${targetId}`;
        const saved = localStorage.getItem(storageKey);
        const newApprovalState = saved ? new Set(JSON.parse(saved)) : new Set();
        
        setApprovedByCurrentParent(newApprovalState);
        
        console.log('🚀 ~ Re-initialized approval state:', {
            storageKey,
            approvals: [...newApprovalState],
            userId: user.id,
            targetId
                 });
    }, [user.id, isParent]);
    
    // Debug effect to track approval state changes
    useEffect(() => {
        console.log('🚀 ~ approvedByCurrentParent state changed:', {
            approvals: [...approvedByCurrentParent],
            timestamp: new Date().toISOString()
        });
    }, [approvedByCurrentParent]);
    
    // Clean up old localStorage entries when component unmounts
    useEffect(() => {
        return () => {
            // Optional: Clean up very old localStorage entries to prevent buildup
            const storageKeys = Object.keys(localStorage);
            const oldApprovalKeys = storageKeys.filter(key => 
                key.startsWith('approved_friends_') && 
                !key.includes(user.id.toString())
            );
            oldApprovalKeys.forEach(key => localStorage.removeItem(key));
        };
    }, [user.id]);
    
    const filterFriends = (e) => {
        setSearchString(e.target.value);
    };
    
    const confirmFriend = async (userId) => {
        try {
            const targetId = isParent && user.children?.length > 0 
            ? window.localStorage.getItem('SELECTED_CHILD_ID') 
            : user.id;
            
            const result = await axiosInstance.post(`/friends/approve/${targetId}/${userId}`);
            
            console.log('Friend approval API response:', {
                status: result.status,
                data: result.data,
                userId,
                targetId
            });
            
            if (result.status === 200 && result.data) {
                const { message, status, approval_details } = result.data;
                
                if (status === 'fully_approved') {
                    // Both parents have approved - remove from pending friends
                    showNotification(message || 'Friendship confirmed!', "success");
                    setPendingFriends(prevFriends => 
                        prevFriends.filter(friend => friend.id !== userId)
                    );
                    // Remove from approved tracking since it's fully processed
                    updateApprovalState(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(userId);
                        return newSet;
                    });
                    // Refresh friends data to reflect the change
                    refreshFriendData();
                } else if (status === 'partial_approval') {
                    // Only one parent has approved - keep in pending but track this parent's approval
                    showNotification(message || 'Your approval recorded. Waiting for other parent\'s approval.', "info");
                    // Track that this parent has approved this request
                    updateApprovalState(prev => {
                        const newSet = new Set(prev);
                        newSet.add(userId);
                        return newSet;
                    });
                    // Don't remove from pending friends list since it's still pending other parent's approval
                } else {
                    console.error('Unexpected status from API:', {
                        status,
                        expectedStatuses: ['fully_approved', 'partial_approval'],
                        fullResponse: result.data
                    });
                    // Handle unexpected status more gracefully
                    if (message) {
                        showNotification(message, "warning");
                    } else {
                        showNotification("Friend approval processed, but status unclear. Please refresh to see current state.", "warning");
                    }
                    // Still refresh data to show current state
                    refreshFriendData();
                }
                
                // Log approval details for debugging
                console.log('Friend approval response processed:', {
                    status,
                    approval_details,
                    userId,
                    message
                });
            } else {
                console.error('Invalid response from friend approval API:', {
                    status: result.status,
                    data: result.data
                });
                showNotification("Error confirming friend - invalid response", "error");
            }
        } catch (error) {
            console.error('Error confirming friend:', {
                error: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            
            // Show more specific error messages based on the error type
            if (error.response?.status === 400) {
                showNotification(error.response.data?.error || "Bad request - you may have already approved this request", "warning");
            } else if (error.response?.status === 403) {
                showNotification(error.response.data?.error || "You are not authorized to approve this friend request", "error");
            } else if (error.response?.status === 404) {
                showNotification(error.response.data?.error || "Friend request not found", "error");
            } else {
                showNotification("Error confirming friend - please try again", "error");
            }
            
            // Refresh data to show current state
            refreshFriendData();
        }
    };
    
    const removeFriend = async (userId) => {
        try {
            const targetId = isParent && user.children?.length > 0 
            ? window.localStorage.getItem('SELECTED_CHILD_ID') 
            : user.id;
            
            const result = await axiosInstance.delete(`/friends/${targetId}/${userId}`);
            if (result.status === 200) {
                console.log('Friend removed:', result.data);
                showNotification("Friend removed", "success");
                
                // Immediately update the UI based on current tab
                if (toggleFriends === 0) {
                    // Remove from pending friends list
                    setPendingFriends(prevFriends => 
                        prevFriends.filter(friend => friend.id !== userId)
                    );
                } else {
                    // Remove from friends list
                    setFriends(prevFriends => 
                        prevFriends.filter(friend => friend.id !== userId)
                    );
                }
                
                // Remove from approved tracking
                updateApprovalState(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(userId);
                    return newSet;
                });
                
                // Refresh data to ensure consistency
                refreshFriendData();
            }
            else {
                console.error('Error removing friend:', result.data);
                showNotification("Error removing friend", "error");
            }
        } catch (error) {
            console.error('Error removing friend:', error);
            showNotification("Error removing friend", "error");
        }
    };
    
    const XCPalRow = useMemo(
        () => (props) => {
            const {
                pal,
                user,
                isParent,
                classes,
                confirmFriend,
                removeFriend,
                toggleFriends,
                style,
            } = props;
            console.log("🚀 ~ XCPals ~ isParent: ", isParent);
            console.log("🚀 ~ XCPals ~ toggleFriends: ", toggleFriends);
            
            // Determine if confirm button should be shown
            const showConfirmButton = isParent && toggleFriends === 0;
            
            // Check if this parent has already approved this request
            const hasCurrentParentApproved = props.approvedByCurrentParent && props.approvedByCurrentParent.has(pal.id);
            
            // Debug logging for approval state
            console.log('🚀 ~ XCPalRow ~ Approval check:', {
                palId: pal.id,
                palName: pal.name,
                hasCurrentParentApproved,
                approvedByCurrentParent: props.approvedByCurrentParent ? [...props.approvedByCurrentParent] : [],
                toggleFriends
            });
            
            // Determine if child user is allowed to chat
            const chatAllowed = user.settings.chat_allowed === 1;
            
            return (
                <Paper
                    elevation={1}
                    style={{ 
                        ...style,
                        padding: '8px',
                        marginBottom: '8px',
                        minHeight: hasCurrentParentApproved && toggleFriends === 0 ? '80px' : '60px'
                    }}
                    className={classes.palrow}
                >
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={3}>
                        <BrAvatar user={pal} theme={theme} className={classes.xcIcon} />
                    </Grid>
                    <Grid item xs={9} style={{ 
                        paddingRight: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        minHeight: '60px'
                    }}>
                        <Typography className={classes.xcName}> {pal.name} </Typography>
                        {hasCurrentParentApproved && toggleFriends === 0 && (
                            <Typography 
                                variant="caption" 
                                style={{ 
                                    color: '#4CAF50', 
                                    fontSize: '11px',
                                    fontStyle: 'italic',
                                    display: 'block',
                                    textAlign: 'left',
                                    marginTop: '2px',
                                    marginBottom: '4px',
                                    lineHeight: '1.2',
                                    overflow: 'visible',
                                    whiteSpace: 'normal',
                                    wordWrap: 'break-word',
                                    maxWidth: '100%'
                                }}
                            >
                                Waiting for other parent's approval
                            </Typography>
                        )}
                        {toggleFriends === 0 ? (
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '5px',
                                marginTop: hasCurrentParentApproved ? '2px' : '5px',
                                flexWrap: 'wrap'
                            }}>
                                {showConfirmButton && (
                                    hasCurrentParentApproved ? (
                                        <Button
                                            theme={theme}
                                            className={classes.button}
                                            disabled={true}
                                            variant="contained"
                                            style={{ 
                                                backgroundColor: '#4CAF50', 
                                                color: 'white',
                                                opacity: 0.9,
                                                width: '85px',
                                                height: '28px',
                                                fontSize: '9px',
                                                fontWeight: 'bold',
                                                textAlign: 'center',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: '0',
                                                minWidth: '85px',
                                                lineHeight: '1'
                                            }}
                                        >
                                            ✓ Approved
                                        </Button>
                                    ) : (
                                        <Button
                                            theme={theme}
                                            className={classes.button}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                confirmFriend(pal.id);
                                            }}
                                            variant="contained"
                                            style={{
                                                width: '75px',
                                                height: '28px',
                                                fontSize: '9px',
                                                fontWeight: 'bold',
                                                textAlign: 'center',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: '0',
                                                minWidth: '75px',
                                                lineHeight: '1'
                                            }}
                                        >
                                            Confirm
                                        </Button>
                                    )
                                )}
                                <Button
                                    theme={theme}
                                    className={classes.button}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeFriend(pal.id);
                                    }}
                                    variant="contained"
                                    style={{
                                        width: '65px',
                                        height: '28px',
                                        fontSize: '9px',
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0',
                                        minWidth: '65px',
                                        lineHeight: '1'
                                    }}
                                >
                                    Delete
                                </Button>
                            </div>
                        )  : (
                    <Link
                        style={{ textDecoration: "none" }}
                        to={`/messenger/${pal.id}`}
                    >
                        {user.name !== "parent" && chatAllowed ? <Button
                            theme={theme}
                            className={classes.button}
                            variant="contained"
                        >
                            {" "}
                            Chat{" "}
                        </Button> : null}
                    </Link>
                )}
                    </Grid>
                </Grid>
                </Paper>
            );
        },
        [theme, confirmFriend, removeFriend, approvedByCurrentParent]
    );
    
    return (
        <div className={classes.homeGrid}>
            <Grid container spacing={1} justifyContent="center">
                <Grid item xs={12}>
                    <Typography className={classes.title}>
                        My XC Pals
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={8} md={6} lg={4} className={classes.palGrid}>
                    <Card className={classes.root}>
                        <div className={classes.toggleContainer}>
                            <Paper className={classes.toggle}>
                                <Chip
                                    onClick={() => setToggleFriends(1)}
                                    className={classes.chip1}
                                    style={
                                    toggleFriends === 1
                                        ? {
                                            zIndex: 1,
                                            color: theme.primaryColor,
                                            backgroundColor: theme.secondaryColor,
                                        }
                                        : { backgroundColor: "#fffefe" }
                                    }
                                    label="My XC Pals"
                                />
                                <Chip
                                    onClick={() => setToggleFriends(0)}
                                    className={classes.chip2}
                                    style={
                                    toggleFriends === 0
                                        ? {
                                            zIndex: 1,
                                            color: theme.primaryColor,
                                            backgroundColor: theme.secondaryColor,
                                        }
                                        : { backgroundColor: "#fffefe" }
                                    }
                                    label="XC Pal Requests"
                                />
                            </Paper>
                        </div>
                        <Paper
                            elevation={5}
                            className={classes.searchField}
                        >
                            <TextField
                                style={{ width: "100%", height: "30px" }}
                                variant={"filled"}
                                placeholder={"Search"}
                                onChange={filterFriends}
                                value={searchString}
                                InputLabelProps={{
                                    style: { height: "30px" },
                                    shrink: "true"
                                }}
                                InputProps={{
                                    style: {
                                        height: "35px",
                                        backgroundColor: "white",
                                        padding: 0,
                                        after: {
                                            content: "none",
                                        },
                                        before: { content: "none" }
                                    },
                                    shrink: "true",
                                    disableUnderline: true,
                                }}
                                inputProps={{
                                    style: { paddingTop: "10px", paddingBottom: "0px" },
                                }}
                            />
                        </Paper>
                        <div className={classes.friendsList}>
                            {toggleFriends
                                ? friends.map((pal) => {
                                    return searchString === "" ||
                                        pal.name.toUpperCase().includes(searchString.toUpperCase()) ? (
                                            <XCPalRow
                                                key={pal.id}
                                                user={user}
                                                isParent={isParent}
                                                pal={pal}
                                                classes={classes}
                                                confirmFriend={confirmFriend}
                                                removeFriend={removeFriend}
                                                toggleFriends={toggleFriends}
                                                approvedByCurrentParent={approvedByCurrentParent}
                                            />
                                            ) : null;
                                        })
                                : pendingFriends.map((pal) => {
                                    return searchString === "" ||
                                        pal.name.toUpperCase().includes(searchString.toUpperCase()) ? (
                                        <XCPalRow
                                            key={pal.id}
                                            user={user}
                                            isParent={isParent}
                                            pal={pal}
                                            classes={classes}
                                            confirmFriend={confirmFriend}
                                            removeFriend={removeFriend}
                                            toggleFriends={toggleFriends}
                                            approvedByCurrentParent={approvedByCurrentParent}
                                        />
                                        ) : null;
                                    })}
                        </div>
                    </Card>
                </Grid>
            </Grid>
        </div>
    );
}